import ApiClient from "./ApiClient";
import PagedResult from "../models/shared/PagedResult";
import FinancingRequestList from "../models/financing-request/FinancingRequestList";
import FinancingRequestStudent from "../models/financing-request/FinancingRequestStudent";
import FinancingRequestBasicInfoAdministrationData from "../models/financing-request/FinancingRequestBasicInfoAdministrationData";
import FinancingRequestDetails from "../models/financing-request/FinancingRequestDetails";
import FinancingRequest from "../models/financing-request/FinancingRequest";
import FinancingRequestFilter from "../models/financing-request/FinancingRequestFilter";
import FinancingRequestFilterValue from "../models/financing-request/FinancingRequestFilterValue";
import FinancingRequestStudentDataInfo from "../models/financing-request/FinancingRequestStudentDataInfo";
import FinancingRequestValidityRange from "../models/financing-request/validity-range/FinancingRequestValidityRange";
import Month from "../models/school-year/Month";
import FinancingRequestDataSubmission from "../models/financing-request/FinancingRequestDataSubmission";

export default class FinancingRequestApiClient {
  apiClient: ApiClient;
  urlPrefix: string = "financing-requests/";

  constructor() {
    this.apiClient = new ApiClient();
  }

  async getFinancingRequests(page?: number, pageItems?: number) {
    const requests = await this.apiClient.get(
      this.urlPrefix + `?page=${page}&pageSize=${pageItems}`
    );

    return requests as PagedResult<FinancingRequestList>;
  }

  async getDetails(financingRequestId: string) {
    const request = await this.apiClient.get(
      this.urlPrefix + `${financingRequestId}/details`
    );

    return request as FinancingRequestDetails;
  }

  async exportDetails(financingRequestId: string) {
    await this.apiClient.download(
      this.urlPrefix + `${financingRequestId}/details/export`
    );
  }

  async getStudentsForNewFinancingRequest(dateFrom: Date) {
    var localOffset = dateFrom.getTimezoneOffset() * 60000;
    var localTime = dateFrom.getTime();
    const students = await this.apiClient.get(
      this.urlPrefix +
        `new/students?dateFrom=${new Date(localTime + localOffset).toJSON()}`
    );

    return students as Array<FinancingRequestStudent>;
  }

  async getStudentsForEditFinancingRequest(id: number, dateFrom: Date) {
    var localOffset = dateFrom.getTimezoneOffset() * 60000;
    var localTime = dateFrom.getTime();
    const students = await this.apiClient.get(
      this.urlPrefix +
        `${id}/edit/students?dateFrom=${new Date(localTime + localOffset).toJSON()}`
    );

    return students as Array<FinancingRequestStudent>;
  }

  async getBasicInfoForNewFinancingRequest() {
    const basicInfo = await this.apiClient.get(
      this.urlPrefix + `new/basic-info`
    );

    return <FinancingRequestBasicInfoAdministrationData>basicInfo;
  }

  async getFinancingRequestForEdit(id: number) {
    const financingRequest = await this.apiClient.get(
      this.urlPrefix + `${id}/edit`
    );
    return <FinancingRequest>financingRequest;
  }

  async getFinancingRequestStudentDataInfo(financingRequestId: number) {
    const requests = await this.apiClient.get(
      this.urlPrefix +
        `student-data-info?financingRequestId=${financingRequestId}`
    );

    return requests as Array<FinancingRequestStudentDataInfo>;
  }

  async getFinancingRequestValidityRangeData(id: number) {
    const financingRequest = await this.apiClient.get(
      this.urlPrefix + `${id}/validity-range-edit`
    );
    return <FinancingRequestValidityRange>financingRequest;
  }

  async getFinancingRequestMonths(id: number) {
    const months = await this.apiClient.get(this.urlPrefix + `${id}/months`);
    return months as Array<Month>;
  }

  async createFinancingRequest(financingRequest: FinancingRequest) {
    await this.apiClient.post(this.urlPrefix, financingRequest);
  }

  async createFinancingRequestDraft(financingRequest: FinancingRequest) {
    const financingRequestId = await this.apiClient.post(
      this.urlPrefix + "draft",
      financingRequest
    );

    return financingRequestId as number;
  }

  async updateFinancingRequestDraft(financingRequest: FinancingRequest) {
    await this.apiClient.put(this.urlPrefix + "draft", financingRequest);
  }

  async finishFinancingRequest(financingRequest: FinancingRequest) {
    await this.apiClient.put(this.urlPrefix, financingRequest);
  }

  async updateFinancingRequestValidityRange(
    financingRequest: FinancingRequestValidityRange
  ) {
    await this.apiClient.put(
      this.urlPrefix + "validity-range",
      financingRequest
    );
  }

  async getSearchFilters() {
    const filters = await this.apiClient.get(`${this.urlPrefix}search/filters`);

    return filters as FinancingRequestFilter;
  }

  async search(
    filter: FinancingRequestFilterValue,
    page?: number,
    pageItems?: number
  ) {
    //console.log(filter.DateFrom);
    var dateFrom =
      filter.DateFrom instanceof Date &&
      !isNaN(Date.parse(filter.DateFrom.toString()))
        ? new Date(filter.DateFrom).toISOString()
        : "";
    var dateTo =
      filter.DateTo instanceof Date &&
      !isNaN(Date.parse(filter.DateTo.toString()))
        ? new Date(filter.DateTo).toISOString()
        : "";
    //console.log(dateFrom);
    const requests = await this.apiClient.get(
      `${this.urlPrefix}search?page=${page}` +
        `&pageSize=${pageItems}` +
        `&schoolYearId=${filter.schoolYearId}` +
        `&DateFrom=${dateFrom}` +
        `&DateTo=${dateTo}`
    );

    return requests as PagedResult<FinancingRequestList>;
  }

  async deleteFinancingRequest(id: number) {
    const resopns = await this.apiClient.delete(this.urlPrefix + `${id}`);
  }

  async getCanAddNewRequest() {
    const respons = await this.apiClient.get(this.urlPrefix + `isNewEnabled`);

    return respons as boolean;
  }

  async getFinancingRequestDataSubmissionByFinancingRequestId(financingRequestId: number) {
    const response = await this.apiClient.get(
      this.urlPrefix +
        `note-document-fetch/${financingRequestId}`
    );

    return response as FinancingRequestDataSubmission;
  }
}
